import { useState, useMemo, useCallback } from "react";
import { Checkbox } from "antd";
import moment from "moment";

/**
 * Custom hook for enhanced Ant Design Table configuration
 * Provides column visibility, sorting, searching, and filtering capabilities
 *
 * @param {Object} options - Configuration options
 * @param {string} options.tableKey - Unique key for localStorage (e.g., 'productTable')
 * @param {Array} options.columns - Array of column definitions
 * @param {Array} options.data - Array of data records
 * @param {string} options.searchValue - Current search value
 *
 * @returns {Object} - Returns table configuration object
 */
const useTableConfig = ({ tableKey = "", columns = [], data = [], searchValue = "" }) => {
  // Validate column structure - ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const validatedColumns = useMemo(() => {
    if (!Array.isArray(columns) || columns.length === 0) {
      return [];
    }

    return columns.map((col, index) => {
      if (!col.key || !col.title || (!col.dataIndex && !col.alwaysVisible)) {
        console.warn(`[useTableConfig] Column at index ${index} is missing required properties (key, title, or dataIndex)`);
      }
      return {
        ...col,
        defaultVisible: col.defaultVisible !== false,
        sortable: col.sortable || false,
        sortType: col.sortType || 'string',
        align: col.align || 'left'
      };
    }).filter(col => col.key && col.title && (col.dataIndex || col.alwaysVisible));
  }, [columns]);

  // Generate sorter function based on column type
  const generateSorter = useCallback((sortType, dataIndex) => {
    return (a, b) => {
      try {
        const aValue = a?.[dataIndex];
        const bValue = b?.[dataIndex];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        switch (sortType) {
          case 'number':
            return Number(aValue) - Number(bValue);
          case 'date':
            return moment(aValue).unix() - moment(bValue).unix();
          case 'string':
          default:
            return String(aValue).localeCompare(String(bValue));
        }
      } catch (error) {
        console.error(`[useTableConfig] Error in sorter for ${dataIndex}:`, error);
        return 0;
      }
    };
  }, []);

  // Initialize column visibility from localStorage
  const getInitialVisibility = useCallback(() => {
    if (!tableKey || validatedColumns.length === 0) {
      return {};
    }

    try {
      const saved = localStorage.getItem(`${tableKey}_visibleColumns`);
      if (saved) {
        const parsed = JSON.parse(saved);
        const visibility = {};
        validatedColumns.forEach(col => {
          visibility[col.key] = parsed[col.key] !== undefined ? parsed[col.key] : col.defaultVisible;
        });
        return visibility;
      }
    } catch (error) {
      console.error(`[useTableConfig] Error loading column visibility:`, error);
    }

    const defaultVisibility = {};
    validatedColumns.forEach(col => {
      defaultVisibility[col.key] = col.defaultVisible;
    });
    return defaultVisibility;
  }, [tableKey, validatedColumns]);

  const [visibleColumns, setVisibleColumns] = useState(getInitialVisibility);

  // Handle column visibility changes
  const handleColumnVisibilityChange = useCallback((columnKey, checked) => {
    if (!tableKey) return;

    try {
      const newVisibleColumns = {
        ...visibleColumns,
        [columnKey]: checked,
      };
      setVisibleColumns(newVisibleColumns);
      localStorage.setItem(`${tableKey}_visibleColumns`, JSON.stringify(newVisibleColumns));
    } catch (error) {
      console.error(`[useTableConfig] Error saving column visibility:`, error);
    }
  }, [visibleColumns, tableKey]);

  // Reset to default visibility
  const resetColumns = useCallback(() => {
    if (!tableKey || validatedColumns.length === 0) return;

    try {
      const defaultVisibility = {};
      validatedColumns.forEach(col => {
        defaultVisibility[col.key] = col.defaultVisible;
      });
      setVisibleColumns(defaultVisibility);
      localStorage.setItem(`${tableKey}_visibleColumns`, JSON.stringify(defaultVisibility));
    } catch (error) {
      console.error(`[useTableConfig] Error resetting columns:`, error);
    }
  }, [tableKey, validatedColumns]);

  // Show all columns
  const showAllColumns = useCallback(() => {
    if (!tableKey || validatedColumns.length === 0) return;

    try {
      const allVisible = {};
      validatedColumns.forEach(col => {
        allVisible[col.key] = true;
      });
      setVisibleColumns(allVisible);
      localStorage.setItem(`${tableKey}_visibleColumns`, JSON.stringify(allVisible));
    } catch (error) {
      console.error(`[useTableConfig] Error showing all columns:`, error);
    }
  }, [tableKey, validatedColumns]);

  // Hide all columns (keep first visible)
  const hideAllColumns = useCallback(() => {
    if (!tableKey || validatedColumns.length === 0) return;

    try {
      const allHidden = {};
      validatedColumns.forEach((col, index) => {
        allHidden[col.key] = index === 0;
      });
      setVisibleColumns(allHidden);
      localStorage.setItem(`${tableKey}_visibleColumns`, JSON.stringify(allHidden));
    } catch (error) {
      console.error(`[useTableConfig] Error hiding columns:`, error);
    }
  }, [tableKey, validatedColumns]);

  // Toggle individual column
  const toggleColumn = useCallback((columnKey) => {
    handleColumnVisibilityChange(columnKey, !visibleColumns[columnKey]);
  }, [visibleColumns, handleColumnVisibilityChange]);

  // Build enhanced columns with sorting and filtering
  const enhancedColumns = useMemo(() => {
    return validatedColumns.map((col, index) => {
      const enhancedCol = { ...col };

      // Preserve custom sorter, filters, onFilter, and defaultSortOrder
      // Add sorter only if sortable and no custom sorter exists
      if (col.sortable && !col.sorter) {
        enhancedCol.sorter = generateSorter(col.sortType, col.dataIndex);
      }

      // Add search filter to first column only if it doesn't have custom filters and searchValue is provided
      if (index === 0 && searchValue !== undefined && searchValue !== '' && !col.filters && !col.onFilter) {
        enhancedCol.filteredValue = [searchValue];
        enhancedCol.onFilter = (value, record) => {
          try {
            return Object.values(record)
              .join(" ")
              .toLowerCase()
              .includes(value.toLowerCase());
          } catch (error) {
            console.error(`[useTableConfig] Error in filter:`, error);
            return false;
          }
        };
      }

      return enhancedCol;
    });
  }, [validatedColumns, searchValue, generateSorter]);

  // Filter columns based on visibility (but always show columns with alwaysVisible: true)
  const displayedColumns = useMemo(() => {
    return enhancedColumns.filter(col => col.alwaysVisible || visibleColumns[col.key]);
  }, [enhancedColumns, visibleColumns]);

  // Generate column configuration menu
  const columnConfigMenu = useMemo(() => {
    const visibleCount = Object.values(visibleColumns).filter(Boolean).length;

    return {
      items: [
        {
          key: 'select-all',
          label: (
            <div className="font-semibold text-blue-600 border-b pb-2 mb-2">
              {visibleCount} of {validatedColumns.length} columns visible
            </div>
          ),
          type: 'group',
        },
        {
          key: 'actions',
          type: 'group',
          label: (
            <div className="flex gap-2 mb-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  showAllColumns();
                }}
                className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border border-blue-300 rounded"
              >
                Show All
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  resetColumns();
                }}
                className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1 border border-gray-300 rounded"
              >
                Reset
              </button>
            </div>
          ),
        },
        ...validatedColumns.map((col) => ({
          key: col.key,
          label: (
            <Checkbox
              checked={visibleColumns[col.key]}
              onChange={(e) => {
                e.stopPropagation();
                handleColumnVisibilityChange(col.key, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {col.title}
            </Checkbox>
          ),
        })),
      ],
    };
  }, [validatedColumns, visibleColumns, handleColumnVisibilityChange, showAllColumns, resetColumns]);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchValue || !Array.isArray(data)) {
      return data;
    }

    try {
      return data.filter(record => {
        return Object.values(record || {})
          .join(" ")
          .toLowerCase()
          .includes(searchValue.toLowerCase());
      });
    } catch (error) {
      console.error(`[useTableConfig] Error filtering data:`, error);
      return data;
    }
  }, [data, searchValue]);

  // Validate and warn about missing required parameters
  if (!tableKey) {
    console.warn("[useTableConfig] tableKey is required for localStorage persistence");
  }

  if (!Array.isArray(columns) || columns.length === 0) {
    console.warn("[useTableConfig] columns must be a non-empty array");
  }

  return {
    visibleColumns: displayedColumns,
    columnConfigMenu,
    filteredData,
    resetColumns,
    toggleColumn,
    showAllColumns,
    hideAllColumns,
    allColumns: enhancedColumns,
  };
};

export default useTableConfig;
